import React, { useState, useEffect, useRef, useMemo } from "react";
import { Data } from "../pages/App";
import { exportVisualDiagram } from "../utils/exportUtils";
import "../style/Visual.css";
import { useNavigate } from "react-router-dom";
import { Puzzle } from "lucide-react";
import {
  coerceToSingleString,
  coerceToExternalFactorList,
  singleStringToFormField,
  DEFAULT_PUZZLE_COLOR,
  syncPuzzleColorsToCount,
} from "../constants/tocFields";

type VisualProps = {
  data: Data;
  updateField: (field: keyof Data, value: string) => void;
  columnColors?: { [field in keyof Data]?: { bg: string; text: string } };
  cloudColors?: { bg: string; text: string }[];
  updateColumnColors?: (colors: {
    [field in keyof Data]?: { bg: string; text: string };
  }) => void;
  updateCloudColors?: (colors: { bg: string; text: string }[]) => void;
  onFieldAdded?: (fieldName: string) => void;
  isLoading?: boolean;
};

type CardConfig = {
  value: string;
};

type ColumnConfig = {
  title: string;
  field: keyof Data;
  cards: CardConfig[];
};

// Loading Spinner Component
const LoadingSpinner = () => {
  return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p className="loading-text">Loading project data...</p>
    </div>
  );
};

// Toast Notification Component
const Toast = ({
  message,
  type = "success",
  onClose,
}: {
  message: string;
  type?: "success" | "error" | "warning";
  onClose: () => void;
}) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  // Icon definitions for different toast types
  const icons = {
    success: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="10"
          cy="10"
          r="9"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
        />
        <path
          d="M6 10L9 13L14 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    error: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="10"
          cy="10"
          r="9"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
        />
        <path
          d="M7 7L13 13M13 7L7 13"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    warning: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 2L2 17h16L10 2z"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10 8v4M10 14v.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  };

  return (
    <div className={`toast-notification toast-${type}`}>
      {icons[type]}
      {message}
    </div>
  );
};

const FootstepsTrail = ({
  count = 3,
  compact = false,
}: {
  count?: number;
  compact?: boolean;
}) => {
  return (
    <div
      className={`footsteps-trail ${compact ? "compact" : ""}`}
      style={{ ["--step-count" as any]: count }}
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={`footstep-pair ${
            index % 2 === 0 ? "left-step" : "right-step"
          }`}
        >
          <div className="footprint">
            <span className="heel" />
            <span className="sole" />
            <span className="toe toe-1" />
            <span className="toe toe-2" />
            <span className="toe toe-3" />
            <span className="toe toe-4" />
          </div>
        </div>
      ))}
    </div>
  );
};


/** Always one visual card per impact column (never split on commas). */
const toSingleCard = (value: string | undefined): CardConfig[] => {
  const text = coerceToSingleString(value);
  return [{ value: text }];
};

const toExternalInfluenceArray = (value: string | undefined): string[] =>
  coerceToExternalFactorList(value);

const puzzlePositions = [
  { left: "18%", top: "8%" },   // left top ellipse
  { left: "42%", top: "5%" },   // top center
  { left: "70%", top: "8%" },   // right top
  { left: "90%", top: "70%" },  // right middle (still outside cards)
  { left: "30%", top: "80%" },  // lower ellipse area
];

const getImpactCardFontSize = (text: string): string => {
  const length = text.trim().length;

  if (length > 180) return "12px";
  if (length > 150) return "12.5px";
  if (length > 120) return "13px";
  if (length > 80) return "14px";
  return "14px";
};

// Main Visual Panel Component
export default function VisualPanel({
  data,
  updateField,
  columnColors,
  cloudColors,
  updateColumnColors = () => {},
  updateCloudColors = () => {},
  onFieldAdded = () => {},
  isLoading = false,
}: VisualProps) {
  const navigate = useNavigate(); // React Router navigation hook

  // State for toggling customization mode
  const [showCustomize, setShowCustomize] = useState(false);

  // State for toast notifications
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning";
  } | null>(null);

  const puzzleRandoms = useMemo(() => {
  return puzzlePositions.map(() => ({
    rotate: Math.random() * 30 - 15,
    scale: 0.8 + Math.random() * 0.4,
    offsetX: Math.random() * 10 - 5,
    offsetY: Math.random() * 10 - 5,
  }));
}, []);

  // Reference to the exportable content div
  const exportRef = useRef<HTMLDivElement>(null);

  // Initial column configuration with default data structure
  const initialColumns: ColumnConfig[] = [
  {
    title: "Activities / Outputs",
    field: "activities",
    cards: [{ value: data.activities || "" }],
  },
  {
    title: "Short-term Impact(s)",
    field: "objectives",
    cards: [{ value: data.objectives || "" }],
  },
  {
    title: "Medium-term Impact(s)",
    field: "aim",
    cards: [{ value: data.aim || "" }],
  },
  {
    title: "Long-term Impact",
    field: "goal",
    cards: [{ value: data.goal || "" }],
  },
];

  // State for column data
  const [columns, setColumns] = useState<ColumnConfig[]>(initialColumns);

  // Default color scheme for columns
  const defaultColumnColors = {
  activities: { bg: "#ffffff", text: "#111111" },
  objectives: { bg: "#ffffff", text: "#111111" },
  aim: { bg: "#ffffff", text: "#111111" },
  goal: { bg: "#ffffff", text: "#111111" },
};

  // Local state for column colors
  const [localColumnColors, setLocalColumnColors] =
    useState<{ [field in keyof Data]?: { bg: string; text: string } }>(
      defaultColumnColors
    );

  // Parse external influences
  const [clouds, setClouds] = useState<string[]>(
  toExternalInfluenceArray(data.externalInfluences)
);

  // Local state for cloud colors
  const [localCloudColors, setLocalCloudColors] = useState<
    { bg: string; text: string }[]
  >(clouds.map(() => ({ ...DEFAULT_PUZZLE_COLOR })));

  // Update local colors when props change
  useEffect(() => {
    if (columnColors) {
      setLocalColumnColors(columnColors);
    }
  }, [columnColors]);

  useEffect(() => {
    if (cloudColors && cloudColors.length > 0) {
      setLocalCloudColors([...cloudColors]);
    }
  }, [cloudColors]);

  // Determine which colors to use
  const effectiveColumnColors =
    columnColors && Object.keys(columnColors).length > 0
      ? columnColors
      : localColumnColors;
  const effectiveCloudColors =
    cloudColors && cloudColors.length > 0 ? cloudColors : localCloudColors;

  const puzzleFillColor =
    (effectiveCloudColors[0] ?? DEFAULT_PUZZLE_COLOR).bg;

  // Update columns when data changes handles both single values and arrays
  useEffect(() => {
    setColumns((cols) =>
      cols.map((col) => {
        return { ...col, cards: toSingleCard(data[col.field]) };
      })
    );

    // Update clouds when external influences change
    const parsedClouds = toExternalInfluenceArray(data.externalInfluences);
setClouds(parsedClouds);

if (!cloudColors) {
  const shared = localCloudColors[0] ?? DEFAULT_PUZZLE_COLOR;
  setLocalCloudColors(
    parsedClouds.map((_, idx) => localCloudColors[idx] ?? shared)
  );
}
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit cloudColors/localCloudColors to avoid reset loops
  }, [data]);

  useEffect(() => {
    const influenceArray = toExternalInfluenceArray(data.externalInfluences);
    setClouds(influenceArray);

    const requiredColorCount = influenceArray.filter((v) => v.trim()).length;
    const currentColors = effectiveCloudColors;

    if (requiredColorCount === 0) {
      return;
    }

    if (currentColors.length !== requiredColorCount) {
      const newColors = syncPuzzleColorsToCount(
        currentColors,
        requiredColorCount
      );

      if (updateCloudColors) {
        updateCloudColors(newColors);
      } else {
        setLocalCloudColors(newColors);
      }
    }
  }, [
    data.externalInfluences,
    cloudColors,
    effectiveCloudColors,
    updateCloudColors,
  ]);

  // Update text content of a specific card
  const handleCardTextChange = (
    colIndex: number,
    cardIndex: number,
    value: string
  ) => {
    const newColumns = [...columns];
    newColumns[colIndex].cards[cardIndex].value = value;
    setColumns(newColumns);
    updateField(
      newColumns[colIndex].field,
      singleStringToFormField(newColumns[colIndex].cards[0]?.value ?? "")
    );
  };

  // Update background or text color for a column
  const handleColumnColorChange = (
    field: keyof Data,
    type: "bg" | "text",
    value: string
  ) => {
    if (updateColumnColors) {
      const newColors = {
        ...effectiveColumnColors,
        [field]: { ...effectiveColumnColors[field], [type]: value },
      };
      updateColumnColors(newColors);
    } else {
      setLocalColumnColors((prev) => ({
        ...prev,
        [field]: { ...prev[field], [type]: value },
      }));
    }
  };

  // Update color for all puzzles at once (customise only)
  const handleCloudColorChange = (type: "bg" | "text", value: string) => {
    const activeCount = Math.max(
      toExternalInfluenceArray(data.externalInfluences).filter((v) => v.trim())
        .length,
      1
    );
    const synced = syncPuzzleColorsToCount(effectiveCloudColors, activeCount);
    const newColors = synced.map((c) => ({ ...c, [type]: value }));

    if (updateCloudColors) {
      updateCloudColors(newColors);
    } else {
      setLocalCloudColors(newColors);
    }
  };

  // Export the visual diagram as an image
  const handleExport = async () => {
    if (exportRef.current) {
      try {
        await exportVisualDiagram(
          exportRef.current,
          data.projectTitle || "Theory-of-Change",
          () => {
            console.log(" Redirecting to subscription page...");
            navigate("/plans");
          }
        );
      } catch (error) {
        console.error("Export failed:", error);
        setToast({
          message: "Export failed. Please try again.",
          type: "error",
        });
      }
    }
  };

  // Show loading spinner while data is being fetched
  if (isLoading) {
    return (
      <div className="right-panel-container">
        <LoadingSpinner />
      </div>
    );
  }

  // Main render
  return (
  <div className="right-panel-container">
    {toast && (
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(null)}
      />
    )}

    <div className="panel-buttons">
      <button
        className="btn customize"
        onClick={() => setShowCustomize(!showCustomize)}
      >
        {showCustomize ? "Hide Customisation" : "Customise"}
      </button>
      <button className="btn export" onClick={handleExport}>
        Export
      </button>
    </div>

    <div ref={exportRef} className="impact-map-export-content">
      <div className="impact-map-canvas">
        {/* <h1 className="impact-map-title">Impact Alignment Map</h1> */}

        <div className="impact-map-legend">
          <div className="legend-item">
              <FootstepsTrail count={1} compact />
              <span>= contribution</span>
            </div>
          {/* <div className="legend-item">
            <Footprints size={22} className="legend-footprints" />
            <span>= contribution</span>
          </div> */}
          <div className="legend-item">
            <Puzzle size={22} className="legend-puzzle" />
            <span>= external factors</span>
          </div>
        </div>

        <div className="ellipse ellipse-1" />
        <div className="ellipse ellipse-2" />
        <div className="ellipse ellipse-3" />
        <div className="ellipse ellipse-4" />

        

        {clouds.map((value, idx) => {
  if (!value.trim()) return null;

  const pos = puzzlePositions[idx];
  const r = puzzleRandoms[idx % puzzleRandoms.length];

  return (
    <div
      key={idx}
      className="floating-puzzle-wrap"
      style={{
        top: pos.top,
        left: pos.left,
        transform: `translate(${r.offsetX}px, ${r.offsetY}px) rotate(${r.rotate}deg) scale(${r.scale})`,
      }}
      title={value || "External influence"}
    >
      <div
        className="floating-puzzle"
        style={{
          color: puzzleFillColor,
        }}
      >
        <Puzzle
          size={95}
          fill={puzzleFillColor}
          color={puzzleFillColor}
          strokeWidth={1.6}
        />

<span className="puzzle-text">
  {value}
</span>
      </div>

      {/* {showCustomize && idx < clouds.length && (
        <div className="puzzle-editor">
          <textarea
            value={value}
            onChange={(e) => handleCloudChange(idx, e.target.value)}
            placeholder="External influence"
          />
          <div className="puzzle-editor-buttons">
            {clouds.length < 10 && idx === clouds.length - 1 && (
              <button className="add-cloud-btn" onClick={() => handleAddCloud(idx)}>
                +
              </button>
            )}
            {clouds.length > 1 && idx === clouds.length - 1 && (
              <button className="remove-cloud-btn" onClick={() => handleRemoveCloud(idx)}>
                −
              </button>
            )}
          </div>
        </div>
      )} */}
    </div>
  );
})}

        <div className="impact-flow-row">
          {columns.map((col, colIndex) => (
            <React.Fragment key={col.title}>
              <div className="impact-column">
                {showCustomize && (
                  <div className="customize-controls-column impact-customize-controls">
                    <label className="color-picker-wrapper">
                      Card:
                      <input
                        type="color"
                        className="color-picker-circle"
                        value={effectiveColumnColors[col.field]?.bg || "#8C6BDC"}
                        onChange={(e) =>
                          handleColumnColorChange(col.field, "bg", e.target.value)
                        }
                      />
                    </label>
                    <label className="color-picker-wrapper">
                      Text:
                      <input
                        type="color"
                        className="color-picker-circle"
                        value={effectiveColumnColors[col.field]?.text || "#ffffff"}
                        onChange={(e) =>
                          handleColumnColorChange(col.field, "text", e.target.value)
                        }
                      />
                    </label>
                  </div>
                )}

                {col.cards.map((card, idx) => (
                  <div
                    key={idx}
                    className={`impact-card impact-card-${colIndex + 1}`}
               style={{
                  backgroundColor: effectiveColumnColors[col.field]?.bg || "#f6f6f6",
                  color: effectiveColumnColors[col.field]?.text || "#111111",
                }}
              >
                    {idx === 0 && (
                      <>
                        <div className="impact-card-title" style={{ color: effectiveColumnColors[col.field]?.text || "#111111" }}>
                             {col.title.toUpperCase()}
                        </div>
                      </>
                    )}

                    {/* <textarea
                      ref={(el) => autoResizeTextarea(el)}
                      className="impact-card-textarea"
                      value={card.value}
                      disabled={!showCustomize}
                      style={{ color: effectiveColumnColors[col.field]?.text || "#111111" }}
                      onChange={(e) => {
                        handleCardTextChange(colIndex, idx, e.target.value);
                        autoResizeTextarea(e.target);
                      }}
                    /> */}

                    <textarea
  className="impact-card-textarea"
  value={card.value}
  disabled={!showCustomize}
  style={{
    color: effectiveColumnColors[col.field]?.text || "#111111",
    fontSize: getImpactCardFontSize(card.value),
  }}
  onChange={(e) => {
    handleCardTextChange(colIndex, idx, e.target.value);
  }}
/>
                  </div>
                ))}
              </div>

{colIndex === 0 && (
                  <div className="footstep-group footstep-group-1">
                    <FootstepsTrail count={2} />
                  </div>
                )}

                {colIndex === 1 && (
                  <div className="footstep-group footstep-group-2">
                    <FootstepsTrail count={2} />
                  </div>
                )}

                {colIndex === 2 && (
                  <div className="footstep-group footstep-group-3">
                    <FootstepsTrail count={2} />
                  </div>
                )}
              

{/* {colIndex === 0 && (
  <div className="footstep-group footstep-group-1">
  <Footprints className="single-foot foot-1" size={25} />
  <Footprints className="single-foot foot-2" size={25} />
</div>
)}

{colIndex === 1 && (
  <div className="footstep-group footstep-group-2">
  <Footprints className="single-foot foot-1" size={25} />
  <Footprints className="single-foot foot-2" size={25} />
  <Footprints className="single-foot foot-1" size={25} />
</div>
)}

{colIndex === 2 && (
  <div className="footstep-group footstep-group-3">
  <Footprints className="single-foot foot-1" size={25} />
  <Footprints className="single-foot foot-2" size={25} />
  <Footprints className="single-foot foot-1" size={25} />
</div>
)} */}

            </React.Fragment>
          ))}
        </div>

        {showCustomize && (
          <div className="cloud-customize-controls impact-puzzle-color-controls">
            <span>Puzzle:</span>
            <input
              type="color"
              className="cloud-color-picker"
              value={puzzleFillColor}
              onChange={(e) => handleCloudColorChange("bg", e.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  </div>
);
}
